import React, { useMemo } from 'react';
import { Button, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  SettingOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  MailOutlined,
  CalendarOutlined,
  StopOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { usePermission } from '../../permissions/PermissionsContext';
import { useAuthStore } from '../../auth/store/authStore';
import type { HostTenantListItem } from '../../tenants/api/tenants';
import { normalizeTenantStatus } from '../../tenants/api/tenants';
import { canResendTenantOnboarding } from '../utils/contactEmail';

const { Text } = Typography;

export interface HostTenantRowActionsProps {
  record: HostTenantListItem;
  rowNumber: number;
  onEdit: (row: HostTenantListItem) => void;
  onApprove: (row: HostTenantListItem) => void;
  onReject: (row: HostTenantListItem) => void;
  onResendOnboarding: (row: HostTenantListItem) => void;
  onLicense: (row: HostTenantListItem) => void;
  onExpire: (row: HostTenantListItem) => void;
  onViewCompany?: (row: HostTenantListItem) => void;
}

const HostTenantRowActions: React.FC<HostTenantRowActionsProps> = ({
  record,
  rowNumber,
  onEdit,
  onApprove,
  onReject,
  onResendOnboarding,
  onLicense,
  onExpire,
  onViewCompany,
}) => {
  const canEdit = usePermission('host.tenants.edit');
  const canApprove = usePermission('host.tenants.approve');
  const canReject = usePermission('host.tenants.reject');
  const canResend = usePermission('host.tenants.resend-onboarding');
  const canManageLicense = usePermission('host.tenants.expire');
  const canExpireTenant = usePermission('host.tenants.edit');
  const canSupportView = usePermission('host.tenants.support-view');
  const isHostUser = useAuthStore((state) => Boolean(state.user?.isHost));

  const status = normalizeTenantStatus(record.status);

  const items = useMemo((): MenuProps['items'] => {
    const built: MenuProps['items'] = [
      {
        key: 'row-number',
        label: (
          <Text strong style={{ fontSize: 13 }}>
            #{rowNumber}
          </Text>
        ),
        disabled: true,
      },
      { type: 'divider' },
    ];

    if (canEdit) {
      built.push({
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => onEdit(record),
      });
    }

    if (
      isHostUser &&
      canSupportView &&
      onViewCompany &&
      (status === 'Active' || status === 'Approved')
    ) {
      built.push({
        key: 'view-company',
        label: 'Switch to company',
        icon: <EyeOutlined />,
        onClick: () => onViewCompany(record),
      });
    }

    if (status === 'Requested') {
      if (canApprove) {
        built.push({
          key: 'approve',
          label: 'Approve',
          icon: <CheckOutlined />,
          onClick: () => onApprove(record),
        });
      }
      if (canReject) {
        built.push({
          key: 'reject',
          label: 'Reject',
          icon: <CloseOutlined />,
          danger: true,
          onClick: () => onReject(record),
        });
      }
    }

    if (canResend && canResendTenantOnboarding(record)) {
      built.push({
        key: 'resend',
        label: 'Resend invite',
        icon: <MailOutlined />,
        onClick: () => onResendOnboarding(record),
      });
    }

    if (status === 'Approved' || status === 'Active') {
      if (status === 'Active' && canManageLicense) {
        built.push({
          key: 'license',
          label: 'License',
          icon: <CalendarOutlined />,
          onClick: () => onLicense(record),
        });
      }
      if (canExpireTenant) {
        built.push({
          key: 'expire',
          label: 'Expire',
          icon: <StopOutlined />,
          danger: true,
          onClick: () => onExpire(record),
        });
      }
    }

    return built;
  }, [
    rowNumber,
    canEdit,
    canApprove,
    canReject,
    canResend,
    canManageLicense,
    canExpireTenant,
    canSupportView,
    isHostUser,
    status,
    record,
    onEdit,
    onViewCompany,
    onApprove,
    onReject,
    onResendOnboarding,
    onLicense,
    onExpire,
  ]);

  const interactiveCount = (items?.length ?? 0) - 2;

  if (interactiveCount <= 0) {
    return (
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        disabled
      />
    );
  }

  return (
    <Dropdown
      menu={{ items, onClick: ({ domEvent }) => domEvent.stopPropagation() }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};

export default HostTenantRowActions;
